import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';

import { type RootState } from '@editor2/reducers/root';
import SaveChangesModal from '@editor2/components/SaveChangesModal';
import { createChangesPayload, hasAnyActiveChanges } from '@editor2/services/unsavedChangesService';
import { saveResourceChanges } from '@editor2/reducers/editor/effects';
import { setShowConflictsModal } from '@editor2/reducers/editor/editorSlice';
import { getRoomNameByLocation } from '@editor2/services/helpersService';
import { type ChangesEntity } from '@editor2/types/changes';
import { type ChangesStatus } from '@editor2/types/changesStatus';
import ConflictResolutionModal from '../ConflictResolutionModal';
import { saveButton, resolveConflictsButton } from './styles';
import { validateChangesPayload } from '@editor2/services/validateChangesPayloadService';
import { validateYAMLConfig } from '@editor2/services/yamlValidationService';
import { showErrorToast } from '@editor2/services/toastService';

interface Props {
	courseId: number;
}

const SaveButton: FC<Props> = (props) => {
	const { courseId } = props;

	const dispatch = useDispatch();
	const course = useSelector((state: RootState) => state.courses[courseId]);
	const { changes, headCommit, conflicts, showConflictsModal, pages } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	const location = useLocation();

	const [isSaveChangesModalShown, setSaveChangesModalShown] = useState(false);

	const isActiveChanges = hasAnyActiveChanges(changes);

	const onSaveWithoutReview = () => {
		const changesPayload = createChangesPayload(course);

		try {
			validateYAMLConfig(course.book.yaml_config);
			validateChangesPayload(changesPayload);

			dispatch(
				saveResourceChanges({ payload: changesPayload, courseId, resourceCommit: headCommit })
			);
		} catch (error) {
			showErrorToast(error.message);
		}
	};

	const checkChangesPresent = (changes: ChangesEntity | ChangesStatus[]) => {
		return Object.keys(changes).some((id) => changes[id].length > 0);
	};

	const checkOtherChangesPresent = (changes: ChangesEntity | ChangesStatus[], itemId: string) => {
		return Object.keys(changes).some((id) => id !== itemId && changes[id]?.length > 0);
	};

	const checkPageElementsChanges = (pageElements) => {
		return Object.keys(changes.elements).some(
			(id) => pageElements.includes(id) && changes.elements[id]?.length > 0
		);
	};

	const checkNotPageElementsChanges = (pageElements) => {
		return Object.keys(changes.elements).some(
			(id) => !pageElements.includes(id) && changes.elements[id]?.length > 0
		);
	};

	// eslint-disable-next-line complexity
	const checkMultipleContextChanges = () => {
		switch (true) {
			case location.pathname.includes('pages'): {
				const pageId = getRoomNameByLocation(location.pathname);
				const pageElements = pages.entities[pageId].elements;

				const currentPageChanges = changes.pages[pageId]?.length > 0;
				const currentPageElementsChanges = checkPageElementsChanges(pageElements);

				const otherPagesChanges = checkOtherChangesPresent(changes.pages, pageId);
				const otherPagesElementsChanges = checkNotPageElementsChanges(pageElements);

				const isMultiplePageChanges =
					(currentPageChanges || currentPageElementsChanges) &&
					(otherPagesChanges || otherPagesElementsChanges);

				return (
					isMultiplePageChanges ||
					changes.toc.length ||
					checkChangesPresent(changes.chapters) ||
					checkChangesPresent(changes.page_groups)
				);
			}

			case location.pathname.includes('page_groups'): {
				const pageGroupId = getRoomNameByLocation(location.pathname);

				const isOnlyCurrentPageGroupChanges =
					changes.page_groups[pageGroupId]?.length > 0 &&
					!checkOtherChangesPresent(changes.page_groups, pageGroupId);

				return (
					!isOnlyCurrentPageGroupChanges ||
					changes.toc.length ||
					checkChangesPresent(changes.chapters) ||
					checkChangesPresent(changes.pages) ||
					checkChangesPresent(changes.elements)
				);
			}

			case location.pathname.includes('chapters'): {
				const chapterId = getRoomNameByLocation(location.pathname);

				const isOnlyCurrentChapterChanges =
					changes.chapters[chapterId]?.length > 0 &&
					!checkOtherChangesPresent(changes.chapters, chapterId);

				return (
					!isOnlyCurrentChapterChanges ||
					changes.toc.length ||
					checkChangesPresent(changes.page_groups) ||
					checkChangesPresent(changes.pages) ||
					checkChangesPresent(changes.elements)
				);
			}

			case location.pathname.includes('courses'):
				return true;

			default:
				return false;
		}
	};

	const setResolveConflictsModalShown = (value: boolean) => {
		dispatch(setShowConflictsModal({ courseId, showModal: value }));
	};

	const onSaveButtonClick = () => {
		const multipleContext = checkMultipleContextChanges();
		return multipleContext ? setSaveChangesModalShown(true) : onSaveWithoutReview();
	};

	return (
		<>
			{!!conflicts?.length && (
				<Button
					variant="danger"
					className={resolveConflictsButton}
					onClick={() => {
						setResolveConflictsModalShown(true);
					}}>
					Resolve conflicts
				</Button>
			)}

			{!conflicts?.length && (
				<Dropdown as={ButtonGroup} className={saveButton}>
					<Button
						variant="primary"
						className="saveButton"
						disabled={!isActiveChanges}
						onClick={() => onSaveButtonClick()}>
						Save
					</Button>

					<Dropdown.Toggle split variant="primary" disabled={!isActiveChanges} />

					<Dropdown.Menu>
						<Dropdown.Item onClick={() => setSaveChangesModalShown(true)}>
							Review and Save
						</Dropdown.Item>
						<Dropdown.Item onClick={() => onSaveWithoutReview()} className="dangerOption">
							Save without review
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
			)}

			{isSaveChangesModalShown && (
				<SaveChangesModal
					onHide={() => setSaveChangesModalShown(false)}
					isShown={isSaveChangesModalShown}
					courseId={courseId}
				/>
			)}

			{showConflictsModal && (
				<ConflictResolutionModal
					onHide={() => setResolveConflictsModalShown(false)}
					isShown={showConflictsModal}
					courseId={courseId}
					setSaveChangesModalShown={setSaveChangesModalShown}
				/>
			)}
		</>
	);
};

export default SaveButton;
